import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Plus, Database, Flag, Navigation, Sparkles, Heart, Wind, Wand2, BookOpen, CreditCard, BarChart3, Video, Brain } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import KaleidoscopeManager from './admin/KaleidoscopeManager';
import CopingToolsManager from './admin/CopingToolsManager';
import BreathworkManager from './admin/BreathworkManager';
import NavigationManager from './admin/NavigationManager';
import FeatureFlagsManager from './admin/FeatureFlagsManager';
import DashboardFeaturesManager from './admin/DashboardFeaturesManager';
import OracleCardsManager from './admin/OracleCardsManager';
import CoursesManager from './admin/CoursesManager';
import MeditationsManager from './admin/MeditationsManager';
import AnalyticsDashboard from './admin/AnalyticsDashboard';
import TelempathyManager from './admin/TelempathyManager';
import { LearningResourcesManager } from './admin/LearningResourcesManager';
import SubscriptionTiersManager from './admin/SubscriptionTiersManager';
import SubscriptionOverview from './admin/SubscriptionOverview';
import ContentManager from './ContentManager';

interface AdminContentManagerProps {
  onClose?: () => void;
}

export default function AdminContentManager({ onClose }: AdminContentManagerProps) {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <Dialog onOpenChange={(open) => !open && onClose?.()}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Admin Portal
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-7xl h-[95vh] flex flex-col">
        <DialogHeader className="border-b pb-4 shrink-0">
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <div className="p-2 rounded-lg bg-primary/10">
              <Database className="h-6 w-6 text-primary" />
            </div>
            Content Management System
            <Badge variant="secondary" className="ml-auto">Admin</Badge>
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <ScrollArea className="w-full border-b shrink-0">
            <TabsList className="inline-flex w-full min-w-max h-auto p-1 bg-muted/50">
              <TabsTrigger value="overview" className="gap-2">
                <Database className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="content" className="gap-2">
                <Settings className="h-4 w-4" />
                App Content
              </TabsTrigger>
              
              {/* Business Section */}
              <div className="h-8 w-px bg-border mx-2" />
              <TabsTrigger value="subscriptions" className="gap-2">
                <CreditCard className="h-4 w-4" />
                Subscriptions
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-2">
                <BarChart3 className="h-4 w-4" />
                Analytics
              </TabsTrigger>
              
              {/* Content Section */}
              <div className="h-8 w-px bg-border mx-2" />
              <TabsTrigger value="oracle" className="gap-2">
                <Sparkles className="h-4 w-4" />
                Oracle
              </TabsTrigger>
              <TabsTrigger value="courses" className="gap-2">
                <BookOpen className="h-4 w-4" />
                Courses
              </TabsTrigger>
              <TabsTrigger value="meditations" className="gap-2">
                <Video className="h-4 w-4" />
                Meditations
              </TabsTrigger>
              <TabsTrigger value="resources" className="gap-2">
                <BookOpen className="h-4 w-4" />
                Resources
              </TabsTrigger>
              
              {/* Tools Section */}
              <div className="h-8 w-px bg-border mx-2" />
              <TabsTrigger value="kaleidoscope" className="gap-2">
                <Wand2 className="h-4 w-4" />
                Kaleidoscope
              </TabsTrigger>
              <TabsTrigger value="tools" className="gap-2">
                <Heart className="h-4 w-4" />
                Coping Tools
              </TabsTrigger>
              <TabsTrigger value="breathwork" className="gap-2">
                <Wind className="h-4 w-4" />
                Breathwork
              </TabsTrigger>
              <TabsTrigger value="telempathy" className="gap-2">
                <Brain className="h-4 w-4" />
                Telempathy
              </TabsTrigger>
              
              {/* System Section */}
              <div className="h-8 w-px bg-border mx-2" />
              <TabsTrigger value="navigation" className="gap-2">
                <Navigation className="h-4 w-4" />
                Navigation
              </TabsTrigger>
              <TabsTrigger value="features" className="gap-2">
                <Plus className="h-4 w-4" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="flags" className="gap-2">
                <Flag className="h-4 w-4" />
                Flags
              </TabsTrigger>
            </TabsList>
          </ScrollArea>
          
          <TabsContent value="overview" className="mt-0 flex-1 min-h-0">
            <ScrollArea className="h-full px-6 py-4">
              <div className="space-y-8">
              {/* Welcome Section */}
              <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-xl p-6 border">
                <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Welcome to Content Management
                </h3>
                <p className="text-muted-foreground mb-4">
                  Control every aspect of your app's content, features, and user experience from one place.
                  All changes are instantly live - no app store updates needed.
                </p>
                <div className="flex gap-2">
                  <Badge variant="secondary">Real-time Updates</Badge>
                  <Badge variant="secondary">Dynamic Publishing</Badge>
                  <Badge variant="secondary">Feature Flags</Badge>
                </div>
              </div>

              {/* Business & Analytics */}
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Business & Analytics
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="hover:shadow-md transition-shadow cursor-pointer border-2" onClick={() => setActiveTab("subscriptions")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-base font-semibold">Subscription Tiers</CardTitle>
                      <div className="p-2 rounded-lg bg-primary/10">
                        <CreditCard className="h-5 w-5 text-primary" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm">
                        Manage subscription plans, pricing, features, and included categories. Control what content each tier can access.
                      </CardDescription>
                      <Button variant="ghost" size="sm" className="mt-3 w-full">
                        Open Manager →
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow cursor-pointer border-2" onClick={() => setActiveTab("analytics")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-base font-semibold">Analytics Dashboard</CardTitle>
                      <div className="p-2 rounded-lg bg-primary/10">
                        <BarChart3 className="h-5 w-5 text-primary" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm">
                        Track user behaviour, feature usage, and engagement metrics. Make data-driven decisions for your app.
                      </CardDescription>
                      <Button variant="ghost" size="sm" className="mt-3 w-full">
                        View Analytics →
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Content Management */}
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Content Library
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("oracle")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Oracle Cards</CardTitle>
                      <Sparkles className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Manage oracle card decks and inspirational messages.
                      </CardDescription>
                    </CardContent>
                  </Card>
              
                  
                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("courses")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Courses</CardTitle>
                      <BookOpen className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Manage video courses and educational content.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("meditations")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Meditations</CardTitle>
                      <Video className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Create guided meditation audio and video content.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("resources")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Resources</CardTitle>
                      <BookOpen className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        External learning resources and references.
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Wellness Tools */}
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  Wellness Tools
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("kaleidoscope")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Kaleidoscope</CardTitle>
                      <Wand2 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Visual meditation and hypnotic experiences.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("tools")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Coping Tools</CardTitle>
                      <Heart className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Therapeutic techniques and strategies.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("breathwork")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Breathwork</CardTitle>
                      <Wind className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Breathing exercises and patterns.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("telempathy")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Telempathy</CardTitle>
                      <Brain className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Psychic development and spiritual tools.
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* System Configuration */}
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  System Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("navigation")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Navigation</CardTitle>
                      <Navigation className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Control app menu structure and routing.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("features")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Dashboard</CardTitle>
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Manage dashboard tiles and features.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("flags")}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Feature Flags</CardTitle>
                      <Flag className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Toggle features and manage A/B testing.
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              </div>
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="content" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-xl p-6 border">
                  <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                    <Settings className="h-5 w-5 text-primary" />
                    Edit All App Text Content
                  </h3>
                  <p className="text-muted-foreground">
                    Edit any text content throughout the entire application. Changes are instant and don't use AI credits.
                  </p>
                </div>
                <ContentManager onClose={() => {}} />
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="subscriptions" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <div className="space-y-6">
                <SubscriptionOverview />
                <SubscriptionTiersManager />
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="analytics" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <AnalyticsDashboard />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="resources" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <LearningResourcesManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="oracle" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <OracleCardsManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="courses" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <CoursesManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="meditations" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <MeditationsManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="kaleidoscope" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <KaleidoscopeManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="tools" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <CopingToolsManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="breathwork" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <BreathworkManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="telempathy" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <TelempathyManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="navigation" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <NavigationManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="features" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <DashboardFeaturesManager />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="flags" className="mt-0 h-full">
            <ScrollArea className="h-full px-6 py-4">
              <FeatureFlagsManager />
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}